/**
 * ⌘/Ctrl+Enter — THE SCREEN'S PRIMARY ACT, FROM INSIDE A TEXT BOX.
 *
 * A form is keyed end to end without the mouse: Tab, Tab, ⌘Enter. That is the
 * point at a counter, where the operator's eyes are on the patient rather than
 * on the screen.
 *
 * AND IT IS THE ONLY KEY THAT CAN WORK THERE. A bare letter fires only with
 * nothing to type into, and an open form is a screen whose caret is in a box
 * from the moment it opens — so an `s` on Save would be printed and dead for the
 * whole of the state it exists in, which is the "not just now" that reads as "no
 * such key". A modifier reaches the button from inside the box.
 *
 * A BUTTON OPTS IN with `data-primary`, and at most one visible enabled primary
 * belongs to a scope; the handler takes the first. A DISABLED one is skipped, so
 * a gated act can never be forced through the keyboard — and the gate is
 * re-checked at the write regardless. This only presses a button that is already
 * pressable.
 *
 * MOST SCREENS DECLARE NONE, deliberately. A patient index, a dispensing board
 * and an inventory tab have no single act that is the point of them, and
 * declaring one to satisfy a legend invents an act. The `?` sheet asks the same
 * predicate and greys its row where the answer is null.
 */
import { useEffect } from "react";
import { topOverlay } from "./overlay-stack";
import { isVisible } from "./typing-target";

/**
 * The button ⌘/Ctrl+Enter would press right now, or null.
 *
 * ONE PREDICATE, because two things ask: the handler below and the `?` sheet.
 * Disabled and invisible are both null — "there is no primary act here" and "it
 * is not pressable yet" are the same answer to "will this key do anything", and
 * the sheet says *not just now* for both.
 */
export function primaryActionIn(scope: ParentNode | null): HTMLButtonElement | null {
  if (!scope) return null;
  const buttons = scope.querySelectorAll<HTMLButtonElement>("button[data-primary]");
  return Array.from(buttons).find((b) => !b.disabled && isVisible(b)) ?? null;
}

/**
 * The scope it resolves against: the surface covering the page, or the page.
 *
 * A dialogue's Save is the dialogue's, not the screen's behind it — the reader
 * can only mean the one they are looking at.
 */
export function primaryActionScope(): ParentNode | null {
  if (typeof document === "undefined") return null;
  return topOverlay() ?? document.body;
}

/** Install once, at the app root. */
export function usePrimaryActionHotkey(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      // ⌘Enter mid-composition (中文輸入) belongs to the candidate window.
      if (e.isComposing || e.defaultPrevented) return;
      const button = primaryActionIn(primaryActionScope());
      if (!button) return;
      e.preventDefault();
      button.click();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);
}
